import React, { useRef, useState, useEffect, useCallback } from 'react';
import { MicVocal, CassetteTape, FunnelPlus, KeyboardMusic } from 'lucide-react';
import { AnalysisData } from '../types';
import { nearestIndex, nearestTimeIndex, HARMONIC_COLORS } from '../utils';

export function HarmonicBars({
  spectrogram, currentTime, formantPos, onFormantChange, showRulers,
  filterRegions, filterActive, onAddFilter, onRemoveFilter, onUpdateFilter, onToggleFilter,
  onLoadLiveRecording, synthProfile, praatFormants, onFormantChangeLive, onPraatFormants
}: {
  spectrogram: AnalysisData['spectrogram'];
  currentTime: number;
  formantPos?: { f1: number; f2: number } | null;
  onFormantChange?: (pos: { f1: number; f2: number }) => void;
  showRulers?: boolean;
  onLoadLiveRecording?: (blob: Blob) => void;
  filterRegions?: { id: number; lo: number; hi: number }[];
  filterActive?: boolean;
  onAddFilter?: () => void;
  onRemoveFilter?: (id: number) => void;
  onUpdateFilter?: (id: number, updates: { lo?: number; hi?: number; gain?: number }) => void;
  onToggleFilter?: () => void;
  synthProfile?: { freq: number; db: number; bw?: number }[] | null;
  praatFormants?: Record<string, { freq: number | null; bw: number | null }> | null;
  onFormantChangeLive?: (pos: { f1: number; f2: number } | null) => void;
  onPraatFormants?: (formants: Record<string, { freq: number | null; bw: number | null }> | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hoveredHarmonic, setHoveredHarmonic] = useState<number | null>(null);
  const [hoveredFilter, setHoveredFilter] = useState<number | null>(null);
  const [hoveredFilterDelete, setHoveredFilterDelete] = useState<number | null>(null);
  const peakPositionsRef = useRef<{ n: number; x: number; y: number; freq: number; db: number }[]>([]);
  const [showSynth, setShowSynth] = useState(true);
  const [showLive, setShowLive] = useState(true);

  // Live mic input + recording
  const [liveActive, setLiveActive] = useState(false);
  const [liveRecording, setLiveRecording] = useState<Blob | null>(null);
  const [liveSavedToast, setLiveSavedToast] = useState(false);
  const liveCtxRef = useRef<AudioContext | null>(null);
  const liveAnalyserRef = useRef<AnalyserNode | null>(null);
  const liveStreamRef = useRef<MediaStream | null>(null);
  const liveDataRef = useRef<Float32Array | null>(null);
  const liveRafRef = useRef<number | null>(null);
  const liveRecChunksRef = useRef<Float32Array[]>([]);
  const liveRecProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const liveSampleRateRef = useRef(48000);

  const toggleLive = useCallback(async () => {
    if (liveActive) {
      // Stop — wait for remaining buffers, then build WAV
      await new Promise(r => setTimeout(r, 250));
      if (liveRafRef.current) cancelAnimationFrame(liveRafRef.current);
      liveRecProcessorRef.current?.disconnect();

      // Encode captured audio as WAV
      const chunks = liveRecChunksRef.current;
      const totalLen = chunks.reduce((s, c) => s + c.length, 0);
      if (totalLen > 0) {
        const merged = new Float32Array(totalLen);
        let off = 0;
        for (const c of chunks) { merged.set(c, off); off += c.length; }
        const sr = liveSampleRateRef.current;
        // Encode WAV
        const buf = new ArrayBuffer(44 + merged.length * 2);
        const v = new DataView(buf);
        const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
        ws(0,'RIFF'); v.setUint32(4,36+merged.length*2,true); ws(8,'WAVE');
        ws(12,'fmt '); v.setUint32(16,16,true); v.setUint16(20,1,true);
        v.setUint16(22,1,true); v.setUint32(24,sr,true); v.setUint32(28,sr*2,true);
        v.setUint16(32,2,true); v.setUint16(34,16,true);
        ws(36,'data'); v.setUint32(40,merged.length*2,true);
        for (let i = 0; i < merged.length; i++) {
          const s = Math.max(-1, Math.min(1, merged[i]));
          v.setInt16(44+i*2, s<0?s*0x8000:s*0x7FFF, true);
        }
        setLiveRecording(new Blob([buf], { type: 'audio/wav' }));
      }

      liveStreamRef.current?.getTracks().forEach(t => t.stop());
      liveCtxRef.current?.close();
      liveCtxRef.current = null;
      liveAnalyserRef.current = null;
      liveStreamRef.current = null;
      liveDataRef.current = null;
      liveRecProcessorRef.current = null;
      setLiveActive(false);
      // Show "Saved" toast next to cassette button
      setLiveSavedToast(true);
      setTimeout(() => setLiveSavedToast(false), 2000);
    } else {
      // Start — overwrites previous recording
      setLiveRecording(null);
      liveRecChunksRef.current = [];
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const ctx = new AudioContext();
        liveSampleRateRef.current = ctx.sampleRate;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 8192;
        analyser.smoothingTimeConstant = 0.7;
        source.connect(analyser);

        // Record via ScriptProcessor (WAV capture)
        const processor = ctx.createScriptProcessor(1024, 1, 1);
        processor.onaudioprocess = (e) => {
          liveRecChunksRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
        };
        source.connect(processor);
        processor.connect(ctx.destination);

        liveCtxRef.current = ctx;
        liveAnalyserRef.current = analyser;
        liveStreamRef.current = stream;
        liveDataRef.current = new Float32Array(analyser.frequencyBinCount);
        liveRecProcessorRef.current = processor;
        setLiveActive(true);
      } catch {
        alert('Microphone access denied.');
      }
    }
  }, [liveActive]);

  // Track currentTime in a ref so the draw callback doesn't need it as a dependency
  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;

  // Track canvas dimensions to avoid expensive resize on every frame
  const canvasSizeRef = useRef<{ w: number; h: number }>({ w: 0, h: 0 });

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !spectrogram) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const targetW = Math.round(rect.width * 2);
    const targetH = Math.round(rect.height * 2);
    // Only resize canvas when dimensions actually change — resizing clears the buffer
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
      canvasSizeRef.current = { w: targetW, h: targetH };
    }
    ctx.setTransform(2, 0, 0, 2, 0, 0);
    const W = rect.width;
    const H = rect.height;
    const padL = 35, padR = 10, padT = 15, padB = 25;
    const plotW = W - padL - padR;
    const plotH = H - padT - padB;

    ctx.fillStyle = '#0d0d1a';
    ctx.fillRect(0, 0, W, H);

    // Find nearest harmonic data to current time (binary search)
    const curTime = currentTimeRef.current;
    const harmonics = spectrogram.harmonics;
    if (!harmonics || harmonics.length === 0) return;

    const hIdx = nearestIndex(harmonics, curTime);
    const nearest = harmonics[hIdx];
    const minD = Math.abs(nearest.t - curTime);
    // Hold the last harmonic data for up to 0.5s (covers gaps between voiced frames)
    if (minD > 0.5 || !nearest.peaks || nearest.peaks.length === 0) {
      ctx.fillStyle = '#555';
      ctx.font = '11px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('No harmonics', W / 2, H / 2);
      return;
    }

    const peaks = nearest.peaks;
    const nPeaks = peaks.length;
    if (nPeaks === 0) return;

    // Fixed axes — don't move between frames
    const maxFreq = 6000;
    const freqToX = (f: number) => padL + (f / maxFreq) * plotW;

    const dbMax = 0;
    const dbMin = -80;
    const dbRange = dbMax - dbMin;
    const dbToY = (db: number) => padT + plotH * (1 - (db - dbMin) / dbRange);
    const baseline = padT + plotH;

    // Y axis labels
    ctx.fillStyle = '#555';
    ctx.font = '9px monospace';
    ctx.textAlign = 'right';
    const dbStep = dbRange > 40 ? 10 : 5;
    for (let db = Math.ceil(dbMin / dbStep) * dbStep; db <= dbMax; db += dbStep) {
      const y = dbToY(db);
      if (y < padT || y > baseline) continue;
      ctx.fillText(`${db}`, padL - 3, y + 3);
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.stroke();
    }

    // X axis frequency labels
    ctx.fillStyle = '#555';
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    const freqStep = maxFreq > 4000 ? 1000 : maxFreq > 2000 ? 500 : 250;
    for (let f = freqStep; f < maxFreq; f += freqStep) {
      const x = freqToX(f);
      if (x > padL && x < W - padR) {
        ctx.fillText(`${f >= 1000 ? (f/1000) + 'k' : f}`, x, baseline + 22);
      }
    }

    // Header
    ctx.fillStyle = '#666';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`F0: ${nearest.f0.toFixed(0)} Hz · ${nPeaks} harmonics`, W / 2, 10);

    // Draw the raw STFT spectrum as a continuous curve.
    const specTimes = spectrogram.times;
    const specData = spectrogram.data;
    const freqStepSpec = spectrogram.freq_step;
    const specDbMin = spectrogram.db_min;
    const specDbMax = spectrogram.db_max;

    const specIdx = nearestTimeIndex(specTimes, curTime);
    const specMinD = specIdx >= 0 ? Math.abs(specTimes[specIdx] - curTime) : Infinity;

    if (specMinD < 0.1 && specData[specIdx]) {
      const col = specData[specIdx];
      const specRange = specDbMax - specDbMin;
      const maxBin = Math.min(col.length, Math.ceil(maxFreq / freqStepSpec));

      // Build path once, reuse for fill + stroke
      const pts: [number, number][] = [];
      for (let fi = 0; fi < maxBin; fi++) {
        const x = freqToX(fi * freqStepSpec);
        const db = specDbMin + col[fi] * specRange;
        pts.push([x, Math.max(padT, Math.min(baseline, dbToY(db)))]);
      }

      // Filled curve
      ctx.beginPath();
      ctx.moveTo(padL, baseline);
      for (const [x, y] of pts) ctx.lineTo(x, y);
      ctx.lineTo(freqToX(maxFreq), baseline);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, padT, 0, baseline);
      grad.addColorStop(0, 'rgba(34, 211, 238, 0.3)');
      grad.addColorStop(1, 'rgba(34, 211, 238, 0.02)');
      ctx.fillStyle = grad;
      ctx.fill();

      // Stroke
      ctx.beginPath();
      for (let i = 0; i < pts.length; i++) {
        if (i === 0) ctx.moveTo(pts[i][0], pts[i][1]);
        else ctx.lineTo(pts[i][0], pts[i][1]);
      }
      ctx.strokeStyle = 'rgba(34, 211, 238, 0.5)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Colored harmonic bands — trace the actual STFT curve around each harmonic
    peakPositionsRef.current = [];
    if (specMinD < 0.15 && specData[specIdx] && peaks.length > 0) {
      const col = specData[specIdx];
      const specRange = specDbMax - specDbMin;

      // Color cycle for harmonics
      const hColors = HARMONIC_COLORS;

      peaks.forEach((peak, i) => {
        const isHovered = hoveredHarmonic === peak.n;

        // Find the frequency range for this harmonic band
        const prevFreq = i > 0 ? peaks[i-1].freq : 0;
        const nextFreq = i < peaks.length - 1 ? peaks[i+1].freq : peak.freq + nearest.f0;
        const bandLo = (peak.freq + prevFreq) / 2;
        const bandHi = (peak.freq + nextFreq) / 2;

        const loFi = Math.max(0, Math.floor(bandLo / freqStepSpec));
        const hiFi = Math.min(col.length - 1, Math.ceil(bandHi / freqStepSpec));

        if (loFi >= hiFi) return;

        // Find the actual visual peak within this band from the spectrum data
        let peakBin = loFi, peakVal = -Infinity;
        for (let fi = loFi; fi <= hiFi; fi++) {
          const v = col[fi] || 0;
          if (v > peakVal) { peakVal = v; peakBin = fi; }
        }
        const actualPeakFreq = peakBin * freqStepSpec;
        const actualPeakDb = specDbMin + peakVal * specRange;
        const peakY = Math.max(padT, Math.min(baseline, dbToY(actualPeakDb)));
        if (peakY >= baseline - 2) return;
        const px = freqToX(actualPeakFreq);

        // Store position for hit-testing (using actual visual position)
        peakPositionsRef.current.push({
          n: peak.n, x: px, y: peakY,
          freq: actualPeakFreq, db: actualPeakDb,
        });

        const rgb = hColors[i % hColors.length];

        // Filled region under curve for this band
        ctx.beginPath();
        ctx.moveTo(freqToX(loFi * freqStepSpec), baseline);
        for (let fi = loFi; fi <= hiFi; fi++) {
          const freq = fi * freqStepSpec;
          if (freq > maxFreq) break;
          const x = freqToX(freq);
          const db = specDbMin + (col[fi] || 0) * specRange;
          const y = Math.max(padT, Math.min(baseline, dbToY(db)));
          ctx.lineTo(x, y);
        }
        ctx.lineTo(freqToX(Math.min(hiFi * freqStepSpec, maxFreq)), baseline);
        ctx.closePath();

        ctx.fillStyle = isHovered
          ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.5)`
          : `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.35)`;
        ctx.fill();

        // Stroke the top edge
        ctx.beginPath();
        for (let fi = loFi; fi <= hiFi; fi++) {
          const freq = fi * freqStepSpec;
          if (freq > maxFreq) break;
          const x = freqToX(freq);
          const db = specDbMin + (col[fi] || 0) * specRange;
          const y = Math.max(padT, Math.min(baseline, dbToY(db)));
          if (fi === loFi) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.strokeStyle = isHovered
          ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 1.0)`
          : `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.7)`;
        ctx.lineWidth = isHovered ? 2.5 : 1.5;
        ctx.stroke();

        // Peak dot (at actual visual peak position)
        ctx.beginPath();
        ctx.arc(px, peakY, isHovered ? 5 : 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = isHovered ? 2 : 1;
        ctx.stroke();

        // Label
        ctx.fillStyle = isHovered ? '#fff' : `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
        ctx.font = isHovered ? 'bold 10px monospace' : '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(`H${peak.n}`, px, Math.max(padT + 5, peakY - 8));

        // Tooltip on hover
        if (isHovered) {
          const ty = Math.max(padT + 30, peakY - 28);
          const text = `${actualPeakFreq.toFixed(0)} Hz · ${actualPeakDb.toFixed(1)} dB`;
          const tw = ctx.measureText(text).width + 14;
          ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
          ctx.beginPath();
          ctx.roundRect(px - tw / 2, ty - 14, tw, 18, 4);
          ctx.fill();
          ctx.fillStyle = '#fff';
          ctx.font = '10px monospace';
          ctx.fillText(text, px, ty - 2);
        }
      });
    }

    // Synth overlay — draw the synth harmonic profile as bell curves
    if (showSynth && synthProfile && synthProfile.length > 0) {
      const synthMaxDb = Math.max(...synthProfile.map(p => p.db));
      for (const p of synthProfile) {
        const cx = freqToX(p.freq);
        const cy = Math.max(padT, Math.min(baseline, dbToY(p.db)));
        if (cy >= baseline - 1) continue;

        // Bell curve shape
        const sigmaHz = Math.max(5, (p.bw || 20) / 2.35);
        const sigmaPx = (sigmaHz / maxFreq) * plotW;
        const extent = sigmaPx * 3;

        ctx.beginPath();
        ctx.moveTo(cx - extent, baseline);
        const steps = Math.max(12, Math.round(extent));
        for (let s = 0; s <= steps; s++) {
          const px = cx - extent + (s / steps) * extent * 2;
          const dist = px - cx;
          const g = Math.exp(-0.5 * (dist / sigmaPx) ** 2);
          ctx.lineTo(px, baseline - (baseline - cy) * g);
        }
        ctx.lineTo(cx + extent, baseline);
        ctx.closePath();
        ctx.fillStyle = 'rgba(250, 204, 21, 0.15)'; // yellow tint
        ctx.fill();

        // Outline
        ctx.beginPath();
        for (let s = 0; s <= steps; s++) {
          const px = cx - extent + (s / steps) * extent * 2;
          const dist = px - cx;
          const g = Math.exp(-0.5 * (dist / sigmaPx) ** 2);
          const py = baseline - (baseline - cy) * g;
          s === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.strokeStyle = 'rgba(250, 204, 21, 0.6)';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Peak dot
        ctx.beginPath();
        ctx.arc(cx, cy, 3, 0, Math.PI * 2);
        ctx.fillStyle = '#facc15';
        ctx.fill();
      }
    }

    // Formant rulers
    if (formantPos && showRulers) {
      const drawRuler = (freqHz: number, label: string, color: string) => {
        const x = freqToX(freqHz);
        if (x < padL || x > W - padR) return;

        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash([5, 3]);
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, baseline);
        ctx.stroke();
        ctx.setLineDash([]);

        // Drag handle
        ctx.beginPath();
        ctx.arc(x, padT + 8, 5, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();

        ctx.fillStyle = color;
        ctx.font = 'bold 10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(label, x, padT - 8);
        ctx.font = '8px monospace';
        ctx.fillText(`${Math.round(freqHz)}Hz`, x, padT - 0);
      };

      drawRuler(formantPos.f1, 'F1', '#f87171');
      drawRuler(formantPos.f2, 'F2', '#60a5fa');
    }
    // Filter regions
    if (filterRegions && filterRegions.length > 0) {
      const filterColors = ['#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];
      filterRegions.forEach((region, ri) => {
        const color = filterColors[ri % filterColors.length];
        const xLo = freqToX(region.lo);
        const xHi = freqToX(region.hi);
        const gain = (region as any).gain ?? 1;
        const bandH = (baseline - padT) * gain;
        const bandTop = baseline - bandH;

        const isFilterHovered = hoveredFilter === region.id;

        // Shaded band — height represents gain
        ctx.fillStyle = isFilterHovered
          ? `${color}55`
          : filterActive ? `${color}33` : `${color}11`;
        ctx.fillRect(xLo, bandTop, xHi - xLo, bandH);

        // Gain drag handle — line at the top of the band
        ctx.strokeStyle = `${color}88`;
        ctx.lineWidth = isFilterHovered ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(xLo, bandTop);
        ctx.lineTo(xHi, bandTop);
        ctx.stroke();

        // Edge lines
        ctx.strokeStyle = isFilterHovered ? color : filterActive ? color : `${color}88`;
        ctx.lineWidth = isFilterHovered ? 3 : 2;
        ctx.beginPath(); ctx.moveTo(xLo, padT); ctx.lineTo(xLo, baseline); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(xHi, padT); ctx.lineTo(xHi, baseline); ctx.stroke();

        // Edge drag handles
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(xLo, padT + 10, 4, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(xHi, padT + 10, 4, 0, Math.PI * 2); ctx.fill();

        // Delete button at bottom center (below graph, in the label area)
        const cx = (xLo + xHi) / 2;
        const cy = baseline + 14;
        const r = 8;
        const isDeleteHovered = hoveredFilterDelete === region.id;
        ctx.fillStyle = isDeleteHovered ? '#f87171' : color;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();

        // Draw funnel shape (lucide "funnel" path, scaled to ~14px centered)
        // Funnel: wide top, narrows to a stem bottom-center
        const s = 0.55; // scale factor
        const ox = cx - 7 * s; // offset to center the 14-unit icon
        const oy = cy - 7 * s;
        ctx.strokeStyle = isDeleteHovered ? '#fff' : '#000';
        ctx.lineWidth = 1.3;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        // Wide top bar
        ctx.moveTo(ox + 1 * s, oy + 2 * s);
        ctx.lineTo(ox + 13 * s, oy + 2 * s);
        // Right side narrows down
        ctx.lineTo(ox + 8.5 * s, oy + 8 * s);
        // Stem down
        ctx.lineTo(ox + 8.5 * s, oy + 12 * s);
        // Stem bottom-left
        ctx.lineTo(ox + 5.5 * s, oy + 12 * s);
        // Stem up
        ctx.lineTo(ox + 5.5 * s, oy + 8 * s);
        // Left side narrows up
        ctx.lineTo(ox + 1 * s, oy + 2 * s);
        ctx.stroke();

        if (isDeleteHovered) {
          // X overlay (top-right area, like lucide funnel-x)
          ctx.lineWidth = 1.8;
          ctx.beginPath();
          ctx.moveTo(cx + 2, cy - 5);
          ctx.lineTo(cx + 6, cy - 1);
          ctx.moveTo(cx + 6, cy - 5);
          ctx.lineTo(cx + 2, cy - 1);
          ctx.stroke();
        }

        // Labels
        ctx.fillStyle = color;
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(region.lo)}`, xLo, baseline + 10);
        ctx.fillText(`${Math.round(region.hi)}`, xHi, baseline + 10);
      });
    }

    // Praat formant lines (F3-F6)
    if (praatFormants) {
      const formantColors: Record<string, string> = {
        F3: '#f97316', // orange
        F4: '#a855f7', // purple
        F5: '#14b8a6', // teal
        F6: '#f43f5e', // rose
      };
      for (const key of ['F3', 'F4', 'F5', 'F6'] as const) {
        const f = praatFormants[key];
        if (!f?.freq) continue;
        const x = freqToX(f.freq);
        if (x < padL || x > W - padR) continue;
        const color = formantColors[key];

        // Dashed vertical line
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 3]);
        ctx.beginPath();
        ctx.moveTo(x, padT);
        ctx.lineTo(x, baseline);
        ctx.stroke();
        ctx.setLineDash([]);

        // Label at top
        ctx.fillStyle = color;
        ctx.font = 'bold 9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(key, x, padT - 3);
        ctx.font = '8px monospace';
        ctx.fillText(`${Math.round(f.freq)}`, x, padT + 10);

        // Bandwidth shading if available
        if (f.bw != null && f.bw > 0) {
          const halfBw = f.bw / 2;
          const xLo = freqToX(f.freq - halfBw);
          const xHi = freqToX(f.freq + halfBw);
          ctx.fillStyle = color + '15'; // very subtle
          ctx.fillRect(xLo, padT, xHi - xLo, baseline - padT);
        }
      }
    }
  }, [spectrogram, formantPos, showRulers, filterRegions, filterActive, hoveredHarmonic, hoveredFilter, hoveredFilterDelete, liveActive, synthProfile, showSynth, praatFormants]);

  // Live mic overlay — captures base frame then overlays live data
  // (Base spectrum is redrawn live from analysis data via drawRef.current())

  // Live draw loop
  useEffect(() => {
    if (!liveActive || !liveAnalyserRef.current || !liveDataRef.current) return;
    const analyser = liveAnalyserRef.current;
    const fftData = liveDataRef.current;
    const sampleRate = liveCtxRef.current?.sampleRate || 48000;

    const drawLive = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Redraw the base spectrum at current playhead position (not frozen)
      drawRef.current();

      if (!showLive) return;

      // Now overlay live data in CSS coordinates with 2x scale
      const rect = canvas.getBoundingClientRect();
      ctx.setTransform(2, 0, 0, 2, 0, 0);
      const W = rect.width;
      const H = rect.height;
      const padL = 35, padR = 10, padT = 15, padB = 25;
      const plotW = W - padL - padR;
      const baseline = H - padB;
      const maxFreq = 6000;
      const dbMax = 0;
      const dbMin = -80;
      const dbRange = dbMax - dbMin;
      const freqToX = (f: number) => padL + (f / maxFreq) * plotW;
      const dbToY = (db: number) => padT + (baseline - padT) * (1 - (db - dbMin) / dbRange);

      // Dim the base
      ctx.fillStyle = 'rgba(13, 13, 26, 0.5)';
      ctx.fillRect(padL, padT, plotW, baseline - padT);

      // Get live FFT data
      analyser.getFloatFrequencyData(fftData);
      const binWidth = sampleRate / analyser.fftSize;

      // Fill
      ctx.beginPath();
      ctx.moveTo(padL, baseline);
      for (let i = 0; i < fftData.length; i++) {
        const freq = i * binWidth;
        if (freq > maxFreq) break;
        const x = freqToX(freq);
        const db = Math.max(dbMin, fftData[i]);
        const y = Math.max(padT, Math.min(baseline, dbToY(db)));
        ctx.lineTo(x, y);
      }
      ctx.lineTo(freqToX(maxFreq), baseline);
      ctx.closePath();
      const grad = ctx.createLinearGradient(0, padT, 0, baseline);
      grad.addColorStop(0, 'rgba(250, 204, 21, 0.25)');
      grad.addColorStop(1, 'rgba(250, 204, 21, 0.02)');
      ctx.fillStyle = grad;
      ctx.fill();

      // Stroke
      ctx.beginPath();
      for (let i = 0; i < fftData.length; i++) {
        const freq = i * binWidth;
        if (freq > maxFreq) break;
        const x = freqToX(freq);
        const db = Math.max(dbMin, fftData[i]);
        const y = Math.max(padT, Math.min(baseline, dbToY(db)));
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = '#facc15';
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // Detect F0 and label harmonics
      // Find strongest peak in vocal range (80-800 Hz) as F0
      const minBin = Math.floor(80 / binWidth);
      const maxBin = Math.min(Math.ceil(800 / binWidth), fftData.length - 1);
      let peakBin = minBin;
      let peakVal = -Infinity;
      for (let i = minBin; i <= maxBin; i++) {
        if (fftData[i] > peakVal) {
          peakVal = fftData[i];
          peakBin = i;
        }
      }
      const liveF0 = peakBin * binWidth;

      if (liveF0 > 60 && peakVal > dbMin + 20) {
        // Label harmonics
        ctx.font = '8px monospace';
        ctx.textAlign = 'center';
        for (let n = 1; n <= 20; n++) {
          const freq = liveF0 * n;
          if (freq > maxFreq) break;
          const hBin = Math.round(freq / binWidth);
          if (hBin >= fftData.length) break;

          // Find local peak near expected harmonic
          const searchRadius = Math.max(1, Math.round(liveF0 * 0.1 / binWidth));
          let localPeak = hBin;
          let localMax = -Infinity;
          for (let j = Math.max(0, hBin - searchRadius); j <= Math.min(fftData.length - 1, hBin + searchRadius); j++) {
            if (fftData[j] > localMax) { localMax = fftData[j]; localPeak = j; }
          }

          const hFreq = localPeak * binWidth;
          const hDb = Math.max(dbMin, fftData[localPeak]);
          const hx = freqToX(hFreq);
          const hy = Math.max(padT, Math.min(baseline, dbToY(hDb)));

          if (hy >= baseline - 2) continue;

          // Dot
          ctx.beginPath();
          ctx.arc(hx, hy, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = '#facc15';
          ctx.fill();

          // Label
          ctx.fillStyle = 'rgba(250, 204, 21, 0.8)';
          ctx.fillText(`H${n}`, hx, hy - 6);
        }

        // Show F0 with note name and cents
        const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const midi = 69 + 12 * Math.log2(liveF0 / 440);
        const roundedMidi = Math.round(midi);
        const cents = Math.round((midi - roundedMidi) * 100);
        const noteName = noteNames[((roundedMidi % 12) + 12) % 12];
        const octave = Math.floor(roundedMidi / 12) - 1;
        const centsStr = cents >= 0 ? `+${cents}` : `${cents}`;

        ctx.fillStyle = '#facc15';
        ctx.font = '9px monospace';
        ctx.textAlign = 'left';
        ctx.fillText(`F0: ${liveF0.toFixed(0)} Hz  ${noteName}${octave} ${centsStr}¢`, padL + 4, padT + 12);
      }

      // "LIVE" label
      ctx.fillStyle = '#facc15';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText('● LIVE', W - padR - 4, padT + 24);

      liveRafRef.current = requestAnimationFrame(drawLive);
    };

    liveRafRef.current = requestAnimationFrame(drawLive);
    return () => {
      if (liveRafRef.current) cancelAnimationFrame(liveRafRef.current);
    };
  }, [liveActive]);

  // Live formant tracking — send mic chunks to Praat every ~250ms
  const smoothedFormants = useRef<Record<string, { freq: number; bw: number }>>({});

  useEffect(() => {
    if (!liveActive) {
      smoothedFormants.current = {};
      return;
    }
    let cancelled = false;
    let pending = false;
    const alpha = 0.35; // smoothing factor (0 = full smooth, 1 = no smooth)

    const poll = async () => {
      if (cancelled || pending) return;
      const chunks = liveRecChunksRef.current;
      if (chunks.length === 0) return;

      // Grab the last ~500ms of audio for stable formant estimation
      const sr = liveSampleRateRef.current;
      const samplesNeeded = Math.ceil(sr * 0.5);
      let totalSamples = 0;
      let startIdx = chunks.length - 1;
      while (startIdx > 0 && totalSamples < samplesNeeded) {
        totalSamples += chunks[startIdx].length;
        startIdx--;
      }
      const recentChunks = chunks.slice(startIdx);
      const totalLen = recentChunks.reduce((s, c) => s + c.length, 0);
      if (totalLen < sr * 0.1) return; // too short

      const merged = new Float32Array(totalLen);
      let off = 0;
      for (const c of recentChunks) { merged.set(c, off); off += c.length; }

      // Encode WAV
      const buf = new ArrayBuffer(44 + merged.length * 2);
      const v = new DataView(buf);
      const ws = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
      ws(0, 'RIFF'); v.setUint32(4, 36 + merged.length * 2, true); ws(8, 'WAVE');
      ws(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
      v.setUint16(22, 1, true); v.setUint32(24, sr, true); v.setUint32(28, sr * 2, true);
      v.setUint16(32, 2, true); v.setUint16(34, 16, true);
      ws(36, 'data'); v.setUint32(40, merged.length * 2, true);
      for (let i = 0; i < merged.length; i++) {
        const s = Math.max(-1, Math.min(1, merged[i]));
        v.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
      }
      const wavBlob = new Blob([buf], { type: 'audio/wav' });

      pending = true;
      try {
        const formData = new FormData();
        formData.append('audio', wavBlob, 'live.wav');
        const resp = await fetch('/api/praat', { method: 'POST', body: formData });
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled || data.error) return;
        if (data.formants) {
          const raw = data.formants;
          // Exponential moving average smoothing
          const smoothed: Record<string, { freq: number | null; bw: number | null }> = {};
          for (const key of ['F1','F2','F3','F4','F5','F6']) {
            const r = raw[key];
            if (!r?.freq) { smoothed[key] = { freq: null, bw: null }; continue; }
            const prev = smoothedFormants.current[key];
            if (prev) {
              const sf = prev.freq + alpha * (r.freq - prev.freq);
              const sb = prev.bw + alpha * ((r.bw || prev.bw) - prev.bw);
              smoothedFormants.current[key] = { freq: sf, bw: sb };
              smoothed[key] = { freq: Math.round(sf), bw: Math.round(sb) };
            } else {
              smoothedFormants.current[key] = { freq: r.freq, bw: r.bw || 80 };
              smoothed[key] = { freq: r.freq, bw: r.bw };
            }
          }
          const s1 = smoothed.F1, s2 = smoothed.F2;
          if (s1?.freq != null && s2?.freq != null) {
            onFormantChangeLive?.({ f1: s1.freq, f2: s2.freq });
          }
          onPraatFormants?.(smoothed);
        }
      } catch { /* server unavailable, skip */ }
      finally { pending = false; }
    };

    const intervalId = setInterval(poll, 250);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      // Clear live formants when stopping
      onPraatFormants?.(null);
    };
  }, [liveActive, onFormantChangeLive, onPraatFormants]);

  // Single RAF loop: redraw only when currentTime changes or draw callback updates
  const drawRef = useRef(draw);
  drawRef.current = draw;
  const lastDrawnTimeRef = useRef<number>(-1);
  const rafIdRef = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const t = currentTimeRef.current;
      // Redraw if time changed by more than ~0.5ms (avoids redundant redraws when paused)
      if (Math.abs(t - lastDrawnTimeRef.current) > 0.0005) {
        lastDrawnTimeRef.current = t;
        drawRef.current();
      }
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
    return () => { cancelled = true; if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
  }, []);

  // Redraw immediately when draw callback itself changes (e.g. spectrogram data, hover state)
  useEffect(() => {
    lastDrawnTimeRef.current = -1; // force redraw on next tick
  }, [draw]);

  // Resize: only re-measure, the RAF loop will pick it up
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => { lastDrawnTimeRef.current = -1; });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  // Dragging state for rulers and filter regions
  const dragging = useRef<{ type: 'f1' | 'f2' | 'filter-lo' | 'filter-hi' | 'filter-move' | 'filter-gain'; filterId?: number; offsetFreq?: number } | null>(null);

  const freqFromMouseX = useCallback((e: React.MouseEvent): number => {
    const canvas = canvasRef.current;
    if (!canvas || !spectrogram) return 0;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const padL = 35, padR = 10;
    const plotW = rect.width - padL - padR;
    const maxFreq = 6000;
    return Math.max(50, Math.min(maxFreq, ((x - padL) / plotW) * maxFreq));
  }, [spectrogram]);

  const handleRulerMouseDown = useCallback((e: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const padL = 35, padR = 10;
    const plotW = rect.width - padL - padR;
    const maxFreq = 6000;
    const freqToXLocal = (f: number) => padL + (f / maxFreq) * plotW;

    // Check filter delete buttons first
    if (filterRegions && onRemoveFilter) {
      for (const region of filterRegions) {
        const loX = freqToXLocal(region.lo);
        const hiX = freqToXLocal(region.hi);
        const cx = (loX + hiX) / 2;
        const cy = rect.height - 25 + 14;  // baseline + 14
        if (Math.abs(mx - cx) < 10 && Math.abs(my - cy) < 10) {
          onRemoveFilter(region.id);
          return;
        }
      }
    }

    // Check filter regions — pick the one with the nearest edge/center
    if (filterRegions && onUpdateFilter) {
      const clickFreq = (mx - padL) / plotW * maxFreq;

      // Check if near the gain drag handle (top edge of the band)
      const localBaseline = rect.height - 25; // padB = 25
      for (const region of filterRegions) {
        const loX = freqToXLocal(region.lo);
        const hiX = freqToXLocal(region.hi);
        const gain = (region as any).gain ?? 1;
        const bandTop = localBaseline - (localBaseline - 15) * gain; // padT = 15
        if (mx >= loX && mx <= hiX && Math.abs(my - bandTop) < 6) {
          dragging.current = { type: 'filter-gain', filterId: region.id };
          return;
        }
      }

      // Find nearest edge across all filters
      let bestEdge: { type: 'filter-lo' | 'filter-hi'; filterId: number; dist: number } | null = null;
      for (const region of filterRegions) {
        const loX = freqToXLocal(region.lo);
        const hiX = freqToXLocal(region.hi);
        const dLo = Math.abs(mx - loX);
        const dHi = Math.abs(mx - hiX);
        if (dLo < 8 && (!bestEdge || dLo < bestEdge.dist)) {
          bestEdge = { type: 'filter-lo', filterId: region.id, dist: dLo };
        }
        if (dHi < 8 && (!bestEdge || dHi < bestEdge.dist)) {
          bestEdge = { type: 'filter-hi', filterId: region.id, dist: dHi };
        }
      }
      if (bestEdge) {
        dragging.current = { type: bestEdge.type, filterId: bestEdge.filterId };
        return;
      }

      // Find narrowest filter containing the click (narrowest = most specific)
      let bestBody: { id: number; width: number } | null = null;
      for (const region of filterRegions) {
        if (clickFreq >= region.lo && clickFreq <= region.hi) {
          const w = region.hi - region.lo;
          if (!bestBody || w < bestBody.width) {
            bestBody = { id: region.id, width: w };
          }
        }
      }
      if (bestBody) {
        const region = filterRegions.find(r => r.id === bestBody!.id)!;
        dragging.current = { type: 'filter-move', filterId: bestBody.id, offsetFreq: clickFreq - region.lo };
        return;
      }
    }

    // Check formant rulers
    if (formantPos && onFormantChange && showRulers) {
      const f1x = freqToXLocal(formantPos.f1);
      const f2x = freqToXLocal(formantPos.f2);
      if (Math.abs(mx - f1x) < 10) {
        dragging.current = { type: 'f1' };
        return;
      }
      if (Math.abs(mx - f2x) < 10) {
        dragging.current = { type: 'f2' };
        return;
      }
    }

    dragging.current = null;
  }, [formantPos, onFormantChange, showRulers, filterRegions, onUpdateFilter]);

  const handleRulerMouseMove = useCallback((e: React.MouseEvent) => {
    // Hover detection when not dragging
    if (!dragging.current) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;

      // Check filter region hover — prefer narrowest containing filter
      let foundFilter: number | null = null;
      let foundFilterDelete: number | null = null;
      if (filterRegions) {
        const padL = 35, padR = 10, padT = 15;
        const plotW = rect.width - padL - padR;
        const maxFreq = 6000;
        const freqAtMouse = ((mx - padL) / plotW) * maxFreq;
        const freqToXLocal = (f: number) => padL + (f / maxFreq) * plotW;
        let narrowest = Infinity;
        for (const region of filterRegions) {
          // Check delete button (circle at top center)
          const loX = freqToXLocal(region.lo);
          const hiX = freqToXLocal(region.hi);
          const btnCx = (loX + hiX) / 2;
          const btnCy = rect.height - 25 + 14; // baseline + 14
          if (Math.abs(mx - btnCx) < 8 && Math.abs(my - btnCy) < 8) {
            foundFilterDelete = region.id;
          }
          // Check body
          if (freqAtMouse >= region.lo && freqAtMouse <= region.hi) {
            const w = region.hi - region.lo;
            if (w < narrowest) {
              narrowest = w;
              foundFilter = region.id;
            }
          }
        }
      }
      setHoveredFilter(foundFilter);
      setHoveredFilterDelete(foundFilterDelete);

      // Check harmonic hover
      let found: number | null = null;
      for (const pp of peakPositionsRef.current) {
        if (Math.abs(mx - pp.x) < 15 && Math.abs(my - pp.y) < 20) {
          found = pp.n;
          break;
        }
      }
      setHoveredHarmonic(found);
      return;
    }
    const freq = freqFromMouseX(e);
    const d = dragging.current;

    if (d.type === 'f1' && formantPos && onFormantChange) {
      onFormantChange({ ...formantPos, f1: freq });
    } else if (d.type === 'f2' && formantPos && onFormantChange) {
      onFormantChange({ ...formantPos, f2: freq });
    } else if (d.filterId != null && filterRegions && onUpdateFilter) {
      const region = filterRegions.find(r => r.id === d.filterId);
      if (!region) return;
      if (d.type === 'filter-gain') {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const localMy = e.clientY - rect.top;
        const bl = rect.height - 25; // padB
        const pt = 15; // padT
        const newGain = Math.max(0.05, Math.min(1, (bl - localMy) / (bl - pt)));
        onUpdateFilter(d.filterId, { gain: newGain });
      } else if (d.type === 'filter-lo') {
        onUpdateFilter(d.filterId, { lo: Math.min(freq, region.hi - 20) });
      } else if (d.type === 'filter-hi') {
        onUpdateFilter(d.filterId, { hi: Math.max(freq, region.lo + 20) });
      } else if (d.type === 'filter-move') {
        const bw = region.hi - region.lo;
        const newLo = freq - (d.offsetFreq || 0);
        onUpdateFilter(d.filterId, { lo: Math.max(50, newLo), hi: Math.max(50 + bw, newLo + bw) });
      }
    }
  }, [formantPos, onFormantChange, freqFromMouseX, filterRegions, onUpdateFilter]);

  const handleRulerMouseUp = useCallback(() => {
    dragging.current = null;
  }, []);

  const handleMouseLeave = useCallback(() => {
    dragging.current = null;
    setHoveredHarmonic(null);
    setHoveredFilter(null);
    setHoveredFilterDelete(null);
  }, []);

  return (
    <div className="harmonic-bars-container">
      <div className="spectrum-toolbar">
        {synthProfile && synthProfile.length > 0 && (
          <button
            className={`spectrum-btn layer-toggle ${showSynth ? 'active' : ''}`}
            onClick={() => setShowSynth(v => !v)}
            title={showSynth ? 'Hide synth' : 'Show synth'}
            style={{ borderColor: showSynth ? '#facc15' : undefined, color: showSynth ? '#facc15' : '#555' }}>
            <KeyboardMusic size={12} />
          </button>
        )}
        {liveActive && (
          <button
            className={`spectrum-btn layer-toggle ${showLive ? 'active' : ''}`}
            onClick={() => setShowLive(v => !v)}
            title={showLive ? 'Hide live' : 'Show live'}
            style={{ borderColor: showLive ? '#f87171' : undefined }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: showLive ? '#f87171' : '#555', display: 'inline-block' }} />
          </button>
        )}
        {filterRegions && filterRegions.length > 0 && onToggleFilter && (
          <button className={`spectrum-btn ${filterActive ? 'active' : ''}`}
            onClick={onToggleFilter}>
            {filterActive ? 'Bypass' : 'Listen'}
          </button>
        )}
        {onAddFilter && (
          <button className="spectrum-btn" onClick={onAddFilter}
            disabled={filterRegions && filterRegions.length >= 5}
            title="Add filter region">
            <FunnelPlus size={12} />
          </button>
        )}
        <button
          className={`spectrum-btn ${liveActive ? 'live-active' : ''}`}
          onClick={toggleLive}
          title={liveActive ? 'Stop live input' : 'Live mic overlay'}>
          <MicVocal size={12} />
        </button>
        {liveRecording && !liveActive && onLoadLiveRecording && (
          <>
            <button
              className="spectrum-btn cassette-btn cassette-fade-in"
              onClick={() => onLoadLiveRecording(liveRecording)}
              title="Load live recording as new analysis">
              <CassetteTape size={12} />
            </button>
            {liveSavedToast && (
              <span className="live-saved-toast">Saved</span>
            )}
          </>
        )}
      </div>
      <canvas ref={canvasRef} className="harmonic-bars-canvas"
        onMouseDown={handleRulerMouseDown}
        onMouseMove={handleRulerMouseMove}
        onMouseUp={handleRulerMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: formantPos ? 'ew-resize' : 'default' }} />
    </div>
  );
}
